/**
 * Build.jsx — Master Builder hub for CDI Collectables (`/build`).
 *
 * A single workspace that surfaces every builder in the app as a tile so
 * users don't have to know which sub-menu holds which builder. Purely a
 * navigation surface — no schema mutations happen here. All existing
 * builder pages continue to work standalone.
 *
 * Tile inventory:
 *   • Collections Builder     → /collections/builder
 *   • Category Builder         → /categories/new  (list of categories → /categories)
 *   • Item Entry (new item)    → /items/new
 *   • Invoice Import           → /invoice
 *   • Master Templates preview → in-page reference card (Wine · Whisky · Spirits)
 *
 * Master Templates preview is read-only and reads from the same backend
 * endpoint every category /fields call hits, so any future template
 * additions show up automatically. Nothing here modifies data.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Layers, Tag, PackagePlus, FileText, BookOpen, ChevronRight } from "lucide-react";
import { useTier } from "@/context/AuthContext";

const MASTER_TYPES = [
  {
    key: "wine",
    label: "Wine",
    tone: "border-[hsl(var(--gold))]",
    accent: "text-[hsl(var(--gold))]",
  },
  {
    key: "whisky",
    label: "Whisky",
    tone: "border-amber-600",
    accent: "text-amber-600",
  },
  {
    key: "spirits",
    label: "Spirits",
    tone: "border-teal-500",
    accent: "text-teal-500",
  },
];

export default function Build() {
  const [counts, setCounts] = useState({ collections: null, categories: null, items: null });
  // masterFields is a map keyed by type_key ("wine" / "whisky" / "spirits")
  // → array of master field_name strings, harvested from the first
  // category of each type. Empty while loading; degrades gracefully to
  // "—" when a type has no categories yet.
  const [masterFields, setMasterFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [colsRes, catsRes, itemsRes] = await Promise.all([
          apiClient.get("/collections"),
          apiClient.get("/categories"),
          apiClient.get("/items"),
        ]);
        if (cancelled) return;
        const collections = colsRes.data || [];
        const categories = catsRes.data || [];
        const items = itemsRes.data || [];
        setCounts({
          collections: collections.length,
          categories: categories.length,
          items: items.length,
        });

        // For each master type, find the first category and fetch its
        // schema to build the master-fields preview. Fire in parallel.
        const previews = await Promise.all(
          MASTER_TYPES.map(async ({ key }) => {
            const parentCol = collections.find(
              (c) => String(c.type_id || "").toLowerCase() === key
            );
            if (!parentCol) return [key, null];
            const firstCat = categories.find((c) => c.collection_id === parentCol.id);
            if (!firstCat) return [key, null];
            try {
              const { data } = await apiClient.get(`/categories/${firstCat.id}/fields`);
              const masters = (data || []).filter((f) => f.master).map((f) => f.field_name);
              return [key, masters];
            } catch {
              return [key, null];
            }
          })
        );
        if (cancelled) return;
        setMasterFields(Object.fromEntries(previews));
      } catch (e) {
        if (!cancelled) setErr("Failed to load builder metadata.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { canInvoiceOCR } = useTier();
  const tiles = [
    {
      to: "/collections/builder",
      icon: Layers,
      title: "Collection Builder",
      body: "Create a new collection — pick a type (Wine / Whisky / Spirits / custom), currency, and identity rules.",
      badge: counts.collections == null ? "" : `${counts.collections} collection${counts.collections === 1 ? "" : "s"}`,
      testid: "build-tile-collection-builder",
    },
    {
      to: "/categories/new",
      icon: Tag,
      title: "Category Builder",
      body: "Define category-specific fields (types, options, order). Master template fields are locked; user fields are freely editable.",
      badge: counts.categories == null ? "" : `${counts.categories} categor${counts.categories === 1 ? "y" : "ies"}`,
      testid: "build-tile-category-builder",
    },
    {
      to: "/items/new",
      icon: PackagePlus,
      title: "Item Entry",
      body: "Add a new item with the dynamic form derived from the chosen category's schema plus Model A quantity block.",
      badge: counts.items == null ? "" : `${counts.items} item${counts.items === 1 ? "" : "s"}`,
      testid: "build-tile-item-entry",
    },
    {
      to: "/invoice",
      icon: FileText,
      title: "Invoice Import",
      body: "Upload a photo or PDF invoice to auto-extract multiple items in one pass (Tesseract OCR).",
      badge: "OCR",
      testid: "build-tile-invoice-import",
    },
    {
      to: "/categories",
      icon: BookOpen,
      title: "Manage Categories",
      body: "Browse and edit existing categories. Rename, reorder fields, or add user-added fields on top of the master template.",
      badge: "",
      testid: "build-tile-manage-categories",
    },
    {
      to: "/collections",
      icon: Layers,
      title: "Manage Collections",
      body: "Rename, delete, or configure an existing collection. Analytics dashboards live on each collection detail.",
      badge: "",
      testid: "build-tile-manage-collections",
    },
  ];
  // CDI Master Rule — hide the Invoice Import tile entirely for non-Premium.
  const visibleTiles = canInvoiceOCR ? tiles : tiles.filter((t) => t.to !== "/invoice");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="page-build">
      <header className="mb-8">
        <p className="text-[10px] uppercase tracking-widest vp-muted mb-2">Builder Workspace</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Build</h1>
        <p className="text-sm vp-muted mt-2 max-w-2xl">
          Every builder in one place. Create collections, define categories,
          add items, or import from an invoice — same code as the standalone
          pages, just gathered here for faster access.
        </p>
      </header>

      {err && (
        <div className="mb-6 p-3 rounded-md border border-[hsl(var(--loss))] text-sm" data-testid="build-error">
          {err}
        </div>
      )}

      <section aria-labelledby="build-tiles-heading" className="mb-10">
        <h2 id="build-tiles-heading" className="sr-only">Builder tiles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 landscape:grid-cols-2 lg:landscape:grid-cols-3 auto-rows-fr gap-4">
          {visibleTiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className="vp-card p-5 hover:border-[hsl(var(--gold))] transition-colors group block min-h-[7rem] h-full"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] flex items-center justify-center">
                    <Icon size={18} className="text-[hsl(var(--gold))]" />
                  </div>
                  {t.badge && (
                    <span className="text-[10px] uppercase tracking-wider vp-muted">
                      {loading && t.badge.match(/collection|categor|item/) ? "…" : t.badge}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-base font-semibold">{t.title}</h3>
                  <ChevronRight size={16} className="vp-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-xs vp-muted mt-1.5 leading-relaxed">{t.body}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="build-master-heading" data-testid="build-master-preview">
        <div className="flex items-baseline justify-between mb-3">
          <h2 id="build-master-heading" className="text-base font-semibold">Master Templates</h2>
          <span className="text-[10px] uppercase tracking-widest vp-muted">read-only reference</span>
        </div>
        <p className="text-xs vp-muted mb-4 max-w-2xl">
          Every category inside Wine, Whisky, and Spirits collections shares
          one canonical field list. Master fields cannot be deleted, but
          they remain optional to fill. User-added fields can be added on
          top and freely deleted.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 landscape:grid-cols-2 md:landscape:grid-cols-3 auto-rows-fr gap-4">
          {MASTER_TYPES.map(({ key, label, tone, accent }) => {
            const fields = masterFields[key];
            return (
              <div
                key={key}
                className={`vp-card p-5 border-l-4 ${tone} min-h-[7rem] h-full`}
                data-testid={`build-master-${key}`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${accent}`}>{label}</h3>
                  <span className="text-[10px] vp-muted">
                    {loading ? "…" : fields == null ? "no category yet" : `${fields.length} fields`}
                  </span>
                </div>
                {loading ? (
                  <p className="text-xs vp-muted">Loading schema…</p>
                ) : fields == null ? (
                  <p className="text-xs vp-muted italic">
                    Create a {label} collection + category to seed the template.
                  </p>
                ) : (
                  <ul className="text-xs space-y-1 leading-snug">
                    {fields.map((n) => (
                      <li
                        key={n}
                        className="vp-muted before:content-['·'] before:mr-2 before:vp-subtle"
                      >
                        {n}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
